'use client';
import React, { useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import WalletConnectTab from './components/WalletConnectTab';
import EmailAuthForm from './components/EmailAuthForm';
import Link from 'next/link';

type AuthMode = 'login' | 'signup';
type AuthTab = 'wallet' | 'email';

export default function SignUpLoginPage() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [tab, setTab] = useState<AuthTab>('wallet');

  return (
    <div
      className="min-h-screen flex"
      style={{ background: 'hsl(var(--background))' }}
    >
      {/* Left brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-5/12 xl:w-1/2 p-10 xl:p-16 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1A6FDB 0%, #1558b8 60%, #1e7ae8 100%)',
          borderRight: '1px solid hsl(var(--border))',
        }}
      >
        {/* BG glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 30% 70%, rgba(245,200,66,0.2) 0%, transparent 60%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          }}
        />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <AppLogo size={40} />
          <span className="text-2xl font-extrabold tracking-tight text-white">
            TOKEN<span style={{ color: '#F5C842' }}>TASK</span>
          </span>
        </div>

        {/* Hero content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center py-12">
          <h2 className="text-4xl xl:text-5xl font-extrabold leading-tight mb-6 text-white">
            Complete Tasks.<br />
            <span style={{ color: '#F5C842' }}>Earn Crypto.</span><br />
            Grow Daily.
          </h2>
          <p className="text-lg mb-8 text-white/80">
            Join 1,250+ participants earning rewards across BSC, ETH, SOL &amp; MATIC networks through simple token transfer tasks.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { id: 'brand-stat-1', value: '$5,000+', label: 'Rewards Sent' },
              { id: 'brand-stat-2', value: '1,250', label: 'Participants' },
              { id: 'brand-stat-3', value: '4', label: 'Networks' },
            ].map((s) => (
              <div
                key={s.id}
                className="p-4 rounded-2xl border text-center"
                style={{ background: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.2)' }}
              >
                <p className="text-xl font-bold tabular-nums" style={{ color: '#F5C842' }}>
                  {s.value}
                </p>
                <p className="text-xs mt-0.5 text-white/70">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-xs text-white/50">
            © 2026 TokenTask · All rights reserved
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-10 lg:py-0">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <AppLogo size={36} />
          <span className="text-xl font-extrabold" style={{ color: 'hsl(var(--text))' }}>
            TOKEN<span style={{ color: 'hsl(var(--primary))' }}>TASK</span>
          </span>
        </div>

        <div className="w-full max-w-md">
          {/* Mode toggle */}
          <div
            className="flex rounded-xl p-1 mb-6 border shadow-sm"
            style={{ background: '#ffffff', borderColor: 'hsl(var(--border))' }}
          >
            {(['login', 'signup'] as AuthMode[]).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => setMode(m)}
                className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
                style={{
                  background: mode === m ? 'hsl(var(--primary))' : 'transparent',
                  color: mode === m ? '#fff' : 'hsl(var(--text-secondary))',
                }}
              >
                {m === 'login' ? 'Sign In' : 'Sign Up'}
              </button>
            ))}
          </div>

          {/* Card */}
          <div
            className="rounded-2xl border p-6 shadow-sm"
            style={{ background: '#ffffff', borderColor: 'hsl(var(--border))' }}
          >
            <h1 className="text-xl font-bold mb-1" style={{ color: 'hsl(var(--text))' }}>
              {mode === 'login' ? 'Welcome back' : 'Create your account'}
            </h1>
            <p className="text-sm mb-5" style={{ color: 'hsl(var(--text-secondary))' }}>
              {mode === 'login' ? 'Sign in to access your tasks and rewards.' : 'Join the network and start earning crypto rewards today.'}
            </p>

            {/* Tab switcher */}
            <div
              className="flex gap-1 p-1 rounded-xl mb-5 border"
              style={{ background: 'hsl(var(--surface-2))', borderColor: 'hsl(var(--border))' }}
            >
              {([
                { key: 'wallet', label: '🔗 Connect Wallet' },
                { key: 'email', label: '✉️ Email' },
              ] as { key: AuthTab; label: string }[]).map((t) => (
                <button
                  key={`tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all duration-150"
                  style={{
                    background: tab === t.key ? '#ffffff' : 'transparent',
                    color: tab === t.key ? 'hsl(var(--text))' : 'hsl(var(--muted))',
                    boxShadow: tab === t.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'wallet' ? <WalletConnectTab /> : <EmailAuthForm mode={mode} />}
          </div>

          {/* Switch mode link */}
          <p className="text-center text-sm mt-4" style={{ color: 'hsl(var(--text-secondary))' }}>
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
              className="font-semibold"
              style={{ color: 'hsl(var(--primary))' }}
            >
              {mode === 'login' ? 'Sign Up' : 'Sign In'}
            </button>
          </p>

          {/* Back to dashboard */}
          <p className="text-center text-xs mt-3">
            <Link href="/dashboard" className="font-medium" style={{ color: 'hsl(var(--muted))' }}>
              ← Back to Dashboard
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}